import { isEmpty, isNil } from 'lodash';
import { default as React, useContext } from 'react';
import { View } from '@shared/components/View';
import { Switch } from '@shared/components/form/switch';
import { MBIconV2 } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTouchableOpacity, MBTouchableOverlay } from '@shared/components/touchable';
import { MBTextBody1, MBTextBody2, MBTextOverlineBold } from '@shared/components/typography';
import { Divider } from '@shared/scenes/workflows/edit/components/optionsMenu/divider';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { ShareQRCodeSection } from './ShareQRCodeSection';

interface Props {
  privateShare?: {
    url: string;
    onCopyQr(data: string): void;
    onCopyLink(uri: string): void;
  };
  canCreatePublicCode: boolean;
  hasPublicQrCode: boolean;
  publicUrl: string | undefined;
  onSwitchPublic(isPublic: boolean): void;
  onCopyPublicQr(data: string): void;
  onCopyPublicLink(uri: string): void;
}

export function ShareQRCode({
  privateShare,
  hasPublicQrCode,
  canCreatePublicCode,
  publicUrl,
  onCopyPublicQr,
  onCopyPublicLink,
  onSwitchPublic,
}: Props) {
  const colors = useContext(ColorThemeContext);
  const hasPublicUrl = !isNil(publicUrl) && !isEmpty(publicUrl);
  const canPrivateShare = !isNil(privateShare);
  const showDivider = canPrivateShare && canCreatePublicCode;
  return (
    <MBTouchableOverlay>
      {canPrivateShare && (
        <>
          <View paddingHorizontal={4} paddingVertical={2}>
            <MBTextOverlineBold color={colors.foregroundInactive}>
              {i18n('qrCode.share.teamAccess')}
            </MBTextOverlineBold>
          </View>
          <ShareQRCodeSection
            url={privateShare.url}
            onCopyQr={privateShare.onCopyQr}
            onCopyLink={privateShare.onCopyLink}
          />
        </>
      )}
      {showDivider && (
        <View paddingHorizontal={2}>
          <Divider />
        </View>
      )}
      {canCreatePublicCode && (
        <View paddingLeft={4} paddingVertical={1}>
          <MBTextOverlineBold color={colors.foregroundInactive}>
            {i18n('qrCode.share.publicAccess')}
          </MBTextOverlineBold>
        </View>
      )}
      {hasPublicQrCode && (
        <>
          <View style={{ backgroundColor: colors.backgroundInactive }} fullWidth>
            <MBTextBody2 style={{ paddingHorizontal: grid(4), paddingVertical: grid(1) }}>
              {i18n('qrCode.share.publicAccessDisclaimer')}
            </MBTextBody2>
          </View>
        </>
      )}
      {canCreatePublicCode && (
        <MBTouchableOpacity onPress={() => onSwitchPublic(!hasPublicQrCode)}>
          <View centerVertically row paddingHorizontal={4} paddingVertical={1}>
            <MBIconV2 name="eye-show" size={24} color={colors.foregroundActive} />
            <MBTextBody1 style={{ flexGrow: 2, marginLeft: grid(2) }}>
              {i18n('qrCode.share.createPublic')}
            </MBTextBody1>
            <Switch value={hasPublicQrCode} />
          </View>
        </MBTouchableOpacity>
      )}
      {hasPublicQrCode && !hasPublicUrl && <MBLoadingIndicator />}
      {hasPublicQrCode && hasPublicUrl && !isEmpty(publicUrl) && (
        <ShareQRCodeSection
          url={publicUrl}
          onCopyQr={onCopyPublicQr}
          onCopyLink={onCopyPublicLink}
        />
      )}
    </MBTouchableOverlay>
  );
}
